import type { EditorBlock, PlanSelectorPlan } from '@/store/editorSlice';

/**
 * WEB-625 / Scale A4: ЕДИНЫЙ резолвер списка планов plan_selector. Канонический
 * `planSelectorPlans` в приоритете, иначе AI-формат `plans`, иначе пусто. Обязан
 * использоваться ВЕЗДЕ (рендер, панель настроек, прайс-гейт-детектор), чтобы
 * «что видно = что редактируется = что гейтит публикацию» не расходились.
 *
 * WEB-1988: функция переехала СЮДА из `editorSlice.ts` и оттуда реэкспортируется
 * (кабинетные потребители адрес импорта не меняли). Причина переезда — граф
 * публичного входа: рендерер блоков зовёт её на странице покупателя, а значит
 * тянул за собой весь срез редактора, а с ним стор проектов и организаций.
 * Тип блока импортируется типом — он стирается на сборке и ребра не создаёт.
 *
 * WEB-2062: файл переехал из `src/store/` СЮДА, в общий код плеера. В `store/`
 * он лежал только по месту рождения (его вырезали из среза) и ради него
 * правило сторожа публичного входа «весь `src/store/` — кабинет» держало
 * поимённое исключение; после переезда исключений у правила нет ни одного.
 * Поведение не менялось; адрес импорта из среза сохранён реэкспортом.
 */
interface AiShapedPlanSelector {
  plans?: PlanSelectorPlan[];
  layout?: 'cards' | 'list';
  defaultPriceId?: string;
}

export function resolvePlanSelectorPlans(block: EditorBlock): PlanSelectorPlan[] {
  const ai = block as EditorBlock & AiShapedPlanSelector;
  return block.planSelectorPlans ?? ai.plans ?? [];
}

/**
 * WEB-1714 (решение владельца 06.10, реестр 2368-Р33) — ЕДИНОЕ правило «какой
 * тариф выбран по умолчанию»: план, чья цена совпадает с ценой по умолчанию
 * (`planSelectorDefaultPriceId`, у ИИ-формата — `defaultPriceId`), иначе
 * первый план. Пустая цена плана с пустым умолчанием не совпадает (WEB-786:
 * у ИИ-пейволла цены планов пусты, пока владелец их не привяжет).
 *
 * Пользуются ДВЕ стороны, и копии правила быть не должно:
 *  - рендерер «Выбора тарифа» — начальная карточка при показе блока
 *    (PlanSelectorBlock в PublicBlockRendererCommon.tsx);
 *  - лестница цены оплаты — ступень «живого выбора нет → цена селектора по
 *    умолчанию» для посетителя, обошедшего экран с селектором
 *    (resolvePriceId.ts). Разойдись они — кнопка при обходе списала бы не
 *    тот тариф, который посетитель увидел бы выбранным, зайди он на экран.
 */
export function planSelectorDefaultIndex(
  plans: readonly PlanSelectorPlan[],
  defaultPriceId: string | undefined,
): number {
  const i = plans.findIndex((p) => p.priceId && p.priceId === defaultPriceId);
  return i >= 0 ? i : 0;
}

/** Цена тарифа по умолчанию; `undefined`, если планов нет или у плана нет цены. */
export function resolvePlanSelectorDefaultPriceId(
  block: EditorBlock,
): string | undefined {
  const plans = resolvePlanSelectorPlans(block);
  const ai = block as EditorBlock & AiShapedPlanSelector;
  const defaultPriceId = block.planSelectorDefaultPriceId ?? ai.defaultPriceId;
  const priceId = plans[planSelectorDefaultIndex(plans, defaultPriceId)]?.priceId;
  return priceId ? priceId : undefined;
}
